/**
 * POS Products Inspection Tab
 *
 * Tab component for inspecting sales events by POS product (finished products).
 * Shows ALL sales events by default; posProductId filter is optional.
 *
 * @see sprint-datagrid-migration Phase 3.4
 */

import { useState, useMemo, useCallback } from 'react';
import { Package } from 'lucide-react';
import {
  DataGrid,
  EmptyState,
  textColumn,
  moneyColumn,
  integerColumn,
  type DataGridColumn,
} from '@bill/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { cn } from '@/lib/utils';
import { useProductSaleEvents } from '../hooks';
import type {
  ProductSaleEvent,
  SaleType,
  PaginationInfo,
} from '@bstock/shared';

interface PosProductsInspectionTabProps {
  siteId?: string;
  startDate: string;
  endDate: string;
  posProductId?: string;
  saleTypes?: SaleType[];
  search?: string;
  onReceiptClick: (receiptId: string) => void;
  showFinancials?: boolean;
}

function SaleTypeBadge({ saleType }: { saleType: SaleType }) {
  const colors: Record<SaleType, string> = {
    STANDARD: 'bg-gray-100 text-gray-700',
    PROMO: 'bg-yellow-100 text-yellow-700',
    EMPLOYEE_MEAL: 'bg-green-100 text-green-700',
    OFFERED: 'bg-purple-100 text-purple-700',
    WASTE: 'bg-red-100 text-red-700',
  };
  const labels: Record<SaleType, string> = {
    STANDARD: 'Standard',
    PROMO: 'Promo',
    EMPLOYEE_MEAL: 'Repas employe',
    OFFERED: 'Offert',
    WASTE: 'Casse',
  };
  return (
    <span className={cn('px-2 py-1 rounded-full text-xs font-medium', colors[saleType])}>
      {labels[saleType]}
    </span>
  );
}

const DEFAULT_LIMIT = 20;

export function PosProductsInspectionTab({
  siteId,
  startDate,
  endDate,
  posProductId,
  saleTypes,
  search,
  onReceiptClick,
  showFinancials = false,
}: PosProductsInspectionTabProps) {
  const currency = useCurrency();
  const { formatDateShort, formatTime } = useFormatDate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const { data, isLoading } = useProductSaleEvents({
    productId: posProductId || undefined,
    productType: 'POS_PRODUCT',
    startDate,
    endDate,
    siteId,
    page,
    limit,
    saleTypes: saleTypes?.length ? saleTypes : undefined,
    search: search || undefined,
  });

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const columns = useMemo<DataGridColumn<ProductSaleEvent>[]>(() => {
    const cols: DataGridColumn<ProductSaleEvent>[] = [
      textColumn<ProductSaleEvent>({
        id: 'receiptDate',
        header: 'Date Ticket',
        sortable: false,
        accessor: (r) => r.receiptDate,
        cell: (r) => (
          <div>
            <div className="text-sm text-gray-900">{formatDateShort(r.receiptDate)}</div>
            <div className="text-xs text-gray-500">{formatTime(r.receiptDate)}</div>
          </div>
        ),
      }),
      textColumn<ProductSaleEvent>({
        id: 'orderId',
        header: 'Order ID',
        sortable: false,
        accessor: 'orderId',
        cell: (r) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReceiptClick(r.receiptId);
            }}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            {r.orderId}
          </button>
        ),
      }),
      textColumn<ProductSaleEvent>({
        id: 'productName',
        header: 'Produit',
        sortable: false,
        accessor: 'productName',
      }),
      integerColumn<ProductSaleEvent>({
        id: 'quantity',
        header: 'Quantite',
        accessor: 'quantity',
        sortable: false,
        align: 'center',
      }),
      textColumn<ProductSaleEvent>({
        id: 'saleType',
        header: 'Type Vente',
        align: 'center',
        sortable: false,
        exportValue: (r) => r.saleType,
        cell: (r) => <SaleTypeBadge saleType={r.saleType} />,
      }),
      textColumn<ProductSaleEvent>({
        id: 'recipeName',
        header: 'Recette',
        sortable: false,
        accessor: (r) => (r.recipeName ? `${r.recipeName} (v${r.recipeVersion})` : null),
        cell: (r) => {
          if (!r.recipeName) {
            return <span className="text-orange-600 font-medium">Non definie</span>;
          }
          return (
            <span className="text-gray-900">
              {r.recipeName} (v{r.recipeVersion})
            </span>
          );
        },
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<ProductSaleEvent>({
          id: 'theoreticalCost',
          header: 'Cout theorique',
          sortable: false,
          accessor: (r) => (r.theoreticalCost !== null ? r.theoreticalCost / 100 : null),
          cell: (r) => (
            <span
              className={cn(
                'text-sm',
                r.theoreticalCost === null ? 'text-orange-600 font-medium' : 'text-gray-900',
              )}
            >
              {r.theoreticalCost !== null
                ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(r.theoreticalCost / 100)
                : 'N/A'}
            </span>
          ),
        }),
      );
    }

    return cols;
  }, [currency, onReceiptClick, showFinancials, formatDateShort, formatTime]);

  const pagination: PaginationInfo = data?.pagination ?? {
    page: 1,
    limit,
    total: 0,
    totalPages: 0,
  };

  const items = data?.items ?? [];
  const isEmpty = !isLoading && items.length === 0;

  if (isEmpty) {
    return (
      <div className="space-y-6">
        <EmptyState
          icon={Package}
          title="Aucun evenement de vente pour cette periode"
          description="Aucune vente n'a ete enregistree durant la periode selectionnee. Modifiez la date ou les filtres pour afficher des resultats."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter info */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        {data?.product ? (
          <>
            <span>Filtre par:</span>
            <span className="font-medium text-gray-900">{data.product.name}</span>
            <span className="text-gray-400">|</span>
          </>
        ) : (
          <>
            <span>Toutes les ventes</span>
            <span className="text-gray-400">|</span>
          </>
        )}
        <span>{pagination.total} evenement{pagination.total > 1 ? 's' : ''}</span>
      </div>

      <DataGrid<ProductSaleEvent>
        data={items}
        columns={columns}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        skeletonRows={8}
        onRowClick={(r) => onReceiptClick(r.receiptId)}
        currency={currency}
        exportable={{
          fileName: { base: 'ventes_produits_pos' },
          sheetName: 'Ventes POS',
          position: 'header',
        }}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          total: pagination.total,
          totalPages: pagination.totalPages,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
        emptyState={
          <div className="py-8 text-center text-sm text-neutral-500">
            Aucun evenement de vente
          </div>
        }
      />
    </div>
  );
}
